import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { Moon, Sun, Menu, X, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import SocialLinks from "@/components/SocialLinks";
import { useConsent } from "@/context/ConsentContext";

function Wordmark() {
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center group">
      <span className="brand-word text-lg sm:text-xl">Smart News Italia</span>
    </Link>
  );
}

function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  return (
    <button
      data-testid="theme-toggle"
      aria-label="Cambia tema"
      title={theme === "dark" ? "Passa al tema chiaro" : "Passa al tema scuro"}
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-border hover:bg-muted transition-colors"
    >
      <Sun className="h-4 w-4 hidden dark:block" />
      <Moon className="h-4 w-4 block dark:hidden" />
    </button>
  );
}

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/archivio", label: "Archivio" },
  { to: "/metodologia", label: "Metodo e trasparenza" },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { openPreferences } = useConsent();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-10">
            <Wordmark />
            <nav className="hidden md:flex items-center gap-7">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors hover:text-primary ${
                      isActive ? "text-primary" : "text-muted-foreground"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              variant="outline"
              size="sm"
              data-testid="header-admin-btn"
              className="hidden sm:inline-flex rounded-sm gap-1.5"
              onClick={() => navigate("/admin")}
            >
              <ShieldCheck className="h-4 w-4" /> Area gestione
            </Button>
            <button
              className="md:hidden h-9 w-9 inline-flex items-center justify-center rounded-sm border border-border"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-border bg-background px-5 py-4 flex flex-col gap-3">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                className="text-sm font-medium text-foreground"
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/admin" onClick={() => setOpen(false)} className="text-sm font-medium text-primary">
              Area gestione
            </Link>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-border mt-24 bg-card">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid gap-8 md:grid-cols-3">
          <div>
            <Wordmark />
            <p className="text-sm text-muted-foreground mt-4 leading-relaxed max-w-xs">
              Smart News Italia raccoglie e traduce in italiano discorsi, dichiarazioni e conferenze
              stampa diffusi da fonti istituzionali, per renderli accessibili al pubblico italiano.
            </p>
            <SocialLinks className="mt-6" />
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-4">
              Esplora
            </h4>
            <div className="flex flex-col gap-2 text-sm">
              <Link to="/archivio" className="hover:text-primary transition-colors">Tutti i discorsi</Link>
              <Link to="/metodologia" className="hover:text-primary transition-colors">Metodo e trasparenza</Link>
              <Link to="/admin" className="hover:text-primary transition-colors">Area gestione</Link>
            </div>
          </div>
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-4">
              Uso dell'intelligenza artificiale
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Le immagini derivano dal video originale indicato come fonte. Traduzione, doppiaggio
              ed eventuale sincronizzazione labiale sono realizzati con strumenti di intelligenza
              artificiale.
            </p>
          </div>
        </div>
        <div className="border-t border-border py-5">
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground mb-3" data-testid="footer-legal-nav">
            <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="/cookie-policy" className="hover:text-primary transition-colors">Cookie Policy</Link>
            <button onClick={openPreferences} className="hover:text-primary transition-colors" data-testid="footer-manage-cookies">Gestisci preferenze cookie</button>
            <Link to="/note-legali" className="hover:text-primary transition-colors">Note legali</Link>
            <Link to="/contatti" className="hover:text-primary transition-colors">Contatti</Link>
          </nav>
          <p className="text-center text-xs text-muted-foreground">
            © {new Date().getFullYear()} Smart News Italia — salvo i diritti sui contenuti di terzi.
          </p>
        </div>
      </footer>
    </div>
  );
}
